import { useMemo, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { DateTime } from "luxon";
import _ from "lodash";

import { LessonRateModel } from "app/models/scheduling/LessonRateModel";
import useSlotInterval from "app/utils/hooks/scheduling/useSlotInterval";
import useCalendarPreferences from "app/utils/hooks/useCalendarPreferences";

interface UseSessionTimingParams {
  watchTimeZone: string;
  watchStartTime: string;
  watchEndTime: string;
  watchLessonRateId: any;
  lessonRateOptions: LessonRateModel[];
}

export function useSessionTiming({
  watchTimeZone,
  watchStartTime,
  watchEndTime,
  watchLessonRateId,
  lessonRateOptions,
}: UseSessionTimingParams) {
  const { setValue } = useFormContext();

  const luxonStartAt = useMemo(() => {
    return DateTime.fromISO(watchStartTime, { zone: watchTimeZone });
  }, [watchStartTime, watchTimeZone]);

  const calculateDuration = useCallback(
    (start: string, end: string) => {
      const startTime = DateTime.fromISO(start, { zone: watchTimeZone });
      const endTime = DateTime.fromISO(end, { zone: watchTimeZone });
      return endTime.diff(startTime, "minutes").toObject().minutes;
    },
    [watchTimeZone],
  );

  const onStartTimeChange = useCallback(
    (changeEvent: any) => {
      const duration = calculateDuration(watchStartTime, watchEndTime);

      const newStartAt = DateTime.fromISO(changeEvent.target.value, {
        zone: watchTimeZone,
      });
      const sessionType = lessonRateOptions.find(
        (lr) => lr.id === watchLessonRateId,
      );
      const durationInMinutes = sessionType
        ? sessionType.durationInMinutes
        : duration;

      if (durationInMinutes > 0) {
        const newEndTime = newStartAt.plus({ minutes: durationInMinutes });
        setValue("end", newEndTime.toISO());
      }
    },
    [watchTimeZone, watchStartTime, watchEndTime, watchLessonRateId, lessonRateOptions, calculateDuration, setValue],
  );

  const onEndTimeChange = useCallback(
    (changeEvent: any) => {
      const newEndAt = DateTime.fromISO(changeEvent.target.value, {
        zone: watchTimeZone,
      });
      const sessionType = lessonRateOptions.find(
        (lr) => lr.id === watchLessonRateId,
      );

      if (!sessionType || !luxonStartAt.isValid) {
        setValue("end", changeEvent.target.value);
        calculateDuration(watchStartTime, changeEvent.target.value);
        return;
      }

      const durationInMinutes = sessionType.durationInMinutes || 0;
      const expectedEndTime = luxonStartAt.plus({ minutes: durationInMinutes });

      if (!expectedEndTime.equals(newEndAt)) {
        setValue("lessonRateId", null);
      }

      setValue("end", changeEvent.target.value);
      calculateDuration(watchStartTime, changeEvent.target.value);
    },
    [watchTimeZone, watchStartTime, watchLessonRateId, lessonRateOptions, luxonStartAt, calculateDuration, setValue],
  );

  const { preferences } = useCalendarPreferences();

  const calculateHours = (step: number, timeFormat: "12h" | "24h") => {
    const dateList: { value: number; label: string }[] = [];

    _.range(0, 24 * 60, step).forEach((value) => {
      const hour = Math.floor(value / 60);
      const minute = value % 60;

      let label: string;
      if (timeFormat === "24h") {
        label = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      } else {
        const ampm = hour >= 12 ? "PM" : "AM";
        if (hour === 0) {
          label = `12:${minute.toString().padStart(2, "0")} ${ampm}`;
        } else if (hour > 12) {
          label = `${hour - 12}:${minute.toString().padStart(2, "0")} ${ampm}`;
        } else {
          label = `${hour}:${minute.toString().padStart(2, "0")} ${ampm}`;
        }
      }

      dateList.push({ value, label });
    });

    return dateList;
  };

  const { step } = useSlotInterval();
  const dateList = calculateHours(step, preferences.timeFormat);

  return { luxonStartAt, dateList, onStartTimeChange, onEndTimeChange };
}
